import React, { forwardRef } from 'react';
import { focusRing, motion } from './tokens';

// Icon-only button. `label` is REQUIRED — it goes on aria-label so screen
// readers can identify the action. Icon is passed as a component.

const VARIANTS = {
  default: 'text.body hover:bg-gray-100 hover:text.heading',
  primary: 'text-purple-600 hover:bg-purple-50',
  danger: 'text-red-600 hover:bg-red-50',
  // On a dark/gradient surface (e.g. the chat header): white icon, and a
  // translucent hover instead of default's near-white bg-gray-100.
  onDark: 'text-white bg-white/20 hover:bg-white/30',
};

const SIZES = {
  sm: 'h-7 w-7',
  md: 'h-9 w-9',
  lg: 'h-11 w-11',
};

const IconButton = forwardRef(function IconButton(
  {
    icon: Icon,
    label,
    variant = 'default',
    size = 'md',
    disabled = false,
    onClick,
    className = '',
    ...rest
  },
  ref,
) {
  if (!label) {
    // Throw at dev time to catch accessibility regressions early. Won't
    // crash production because the next render will surface the same error.
    if (process.env.NODE_ENV !== 'production') {
      console.error('IconButton: `label` prop is required for accessibility');
    }
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={[
        // p-0 is load-bearing, not cosmetic. The global scaffold rule in
        // index.css sets `button { padding: 0.6em 1.2em }` (38.4px of
        // horizontal padding). Under border-box that padding alone exceeds
        // every SIZES width, so the width clamps UP to ~40px and the content
        // box collapses to 0 — which shrank the flex-item <svg> to zero width
        // and made every icon button render as an empty box. Sizing here is
        // meant to come from SIZES + flex centering alone.
        'inline-flex items-center justify-center rounded-lg p-0',
        motion,
        focusRing,
        VARIANTS[variant] || VARIANTS.default,
        SIZES[size] || SIZES.md,
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {/* shrink-0: belt-and-braces against the flex-shrink-to-zero failure
          the p-0 above fixes — an icon should never be squeezed away. */}
      {Icon ? <Icon className="w-4 h-4 shrink-0" aria-hidden="true" /> : null}
    </button>
  );
});

export default IconButton;
